/**
 * Types shared with the API, which shares them with `schema/sheet.schema.json`.
 *
 * Duplicated rather than imported because the browser bundle should not pull in
 * the Lambda's dependencies. `solver/tests/test_schema.py` is what stops the
 * three copies drifting: it reads the enums out of the schema and out of
 * `api/src/lib/model.ts` and fails when they disagree.
 */

export type SheetState =
  | 'awaiting_photo'
  | 'prechecking'
  | 'processing'
  | 'needs_review'
  | 'refused'
  | 'approved'
  | 'printed'
  | 'voided'
  | 'error';

export type RefusalAction = 'reshoot' | 'stop';

export interface Refusal {
  code: string;
  message: string;
  action: RefusalAction;
  detail?: Record<string, unknown>;
}

export interface Job {
  jobId: string;
  name: string;
  mesh: number;
  printerId: string;
  /** The design file's own name, kept beside the editable label (SLD-0004). */
  filename?: string;
  artworkKey?: string;
  artworkUploadedAt?: string;
  thumbnailKey?: string;
  thumbnailUploadedAt?: string;
  /** Short-lived signed links the API adds; absent until the file exists. */
  thumbnailUrl?: string;
  artworkUrl?: string;
  createdAt: string;
  createdBy: string;
  sheetCount: number;
  /** Hidden from the jobs list. Nothing is deleted; see api/src/archive-job.ts. */
  archivedAt?: string | null;
  archivedBy?: string | null;
}

export interface Sheet {
  jobId: string;
  sheetId: string;
  state: SheetState;
  createdAt: string;
  updatedAt: string;
  photoKey?: string;
  pdfKey?: string;
  overlays?: string[];
  refusal?: Refusal;
  approvedBy?: string;
  approvedAt?: string;
  reviewedCrops?: string[];
}

/**
 * Fiducial geometry for one bed, in millimetres (B2).
 *
 * snake_case on purpose, and not a slip: these are the keys the Python solver
 * reads off the printer record, the keys in `schema/sheet.schema.json` and the
 * keys the API writes. A second spelling on this screen would be a second thing
 * to keep in step, for numbers that silently rescale every sheet.
 */
export interface PrinterCalibration {
  bar_length_mm: number;
  bar_width_mm?: number | null;
  bar_thickness_mm?: number | null;
  /** Signed: +ve is proud of the mesh plane, -ve is sunk below it, 0 is flush. */
  bar_standoff_mm?: number | null;
  camera_distance_mm?: number | null;
}

export interface PrinterSummary {
  printerId: string;
  name: string;
  surveyedAt: string | null;
  residualMil: number | null;
  markers: number;
  surveyError: { code: string; message: string } | null;
  /** False means every sheet on this printer is refused before any work. */
  calibrated: boolean;
}

/**
 * Where the phone's lens measurement stands (2026-10-02). Returned beside the
 * survey by the printer endpoints, for the workstation and the phone alike.
 */
export interface LensStatus {
  lensRequestedAt: string | null;
  lensPhotos: number;
  lensPhotosReceived: number;
  lensError: { code: string; message: string } | null;
  lens: {
    created: string;
    photosUsed: number;
    corners: number;
    /** Fraction of the frame the board reached, 0..1. */
    coverage: number;
    /** A photo predicted by a model fitted without it, in pixels. */
    heldOutPx: number;
  } | null;
  /** The survey was made through another lens correction: sheets are refused. */
  surveyStale: boolean;
}

/**
 * Is a lens measurement under way for the latest board upload? The same rule
 * as `surveyPending`: an answer is a lens no older than the request, or an
 * error, and nothing is pending once the upload links have expired.
 */
export function lensPending(status: LensStatus, now = Date.now()): boolean {
  if (!status.lensRequestedAt || status.lensError) return false;
  const requested = Date.parse(status.lensRequestedAt);
  if (!Number.isFinite(requested) || now - requested > SURVEY_WAIT_MS) return false;
  if (!status.lens) return true;
  return !(Date.parse(status.lens.created) >= Math.floor(requested / 1000) * 1000);
}

/** The lid's USB camera or the phone; the same values as the API's. */
export type CaptureCamera = 'usb' | 'phone';

export interface PrinterDetail extends SurveyStatus, LensStatus {
  printerId: string;
  name: string;
  updatedAt: string | null;
  calibration: PrinterCalibration | null;
  /** Which capture the job page offers; null offers both. */
  camera: CaptureCamera | null;
}

/**
 * Where a printer's survey stands. The Printers screen and the phone's
 * calibration page (F11) both get exactly this from the API.
 */
export interface SurveyStatus {
  /** When a calibration upload was last handed out, to the millisecond. */
  calibrationRequestedAt: string | null;
  /** Photographs the current calibration asks for, and how many have landed. */
  calibrationPhotos: number;
  calibrationPhotosReceived: number;
  surveyError: { code: string; message: string } | null;
  survey: {
    created: string;
    version: string;
    markers: number;
    crossesFound: number;
    residualMil: number;
    residualMaxMil: number;
    /** Photographs the survey was made from; 1 before 2026-10-02. */
    photos: number;
    /** How far those photographs disagree about a marker; null from one. */
    markerSpreadMil: number | null;
    notes: string[];
    movedSinceLast: {
      worstMarker: string;
      worstMil: number;
      markersCompared: number;
      /** Moves above this are more than the two surveys' noise: a bar moved. */
      thresholdMil: number;
      /** The survey this one is compared against; null if it predates the field. */
      previousCreated: string | null;
    } | null;
  } | null;
}

/**
 * How long after an upload was handed out a missing result still means "the
 * survey is running", rather than "the photographs never arrived". The upload
 * links expire after 30 minutes, so nothing can start later than that.
 */
export const SURVEY_WAIT_MS = 30 * 60 * 1000;

/** What `calibration-upload` hands out: one link per photograph. */
export interface CalibrationSession {
  session: string;
  photos: number;
  uploads: { url: string; key: string }[];
  expiresInSeconds: number;
}

/**
 * Photographs the pending calibration still needs before it can be measured,
 * or 0 when it has them all (or nothing is pending). The survey only starts
 * once every photograph has landed, so "measuring" before then is a lie.
 */
export function photosStillNeeded(status: SurveyStatus, now = Date.now()): number {
  if (!surveyPending(status, now)) return 0;
  return Math.max(0, status.calibrationPhotos - status.calibrationPhotosReceived);
}

/**
 * Is a survey under way for the most recent calibration upload?
 *
 * Decided from the server's own timestamps, never from whether *this* screen
 * started an upload: the photograph may come from the phone while the
 * workstation watches. A survey answers the request if it was created no
 * earlier than the request -- compared to the second, because the solver
 * stamps `created` to the second and the API stamps the request to the
 * millisecond. A survey that predates the request is the *previous* one,
 * which is exactly what the old check (any survey at all) mistook for the
 * answer.
 */
export function surveyPending(status: SurveyStatus, now = Date.now()): boolean {
  if (!status.calibrationRequestedAt || status.surveyError) return false;
  const requested = Date.parse(status.calibrationRequestedAt);
  if (!Number.isFinite(requested) || now - requested > SURVEY_WAIT_MS) {
    return false;
  }
  if (!status.survey) return true;
  const created = Date.parse(status.survey.created);
  return !(created >= Math.floor(requested / 1000) * 1000);
}

/** States in which the operator is expected to do something at the bed. */
export const WAITING_STATES: SheetState[] = ['awaiting_photo', 'refused', 'error'];

/** States that mean the solver is still working; the UI polls through these. */
export const BUSY_STATES: SheetState[] = ['prechecking', 'processing'];

export function isBusy(state: SheetState): boolean {
  return BUSY_STATES.includes(state);
}

/** Plain English for a state, for a person who has not read the schema. */
export function stateLabel(state: SheetState): string {
  return {
    awaiting_photo: 'Waiting for a photo',
    prechecking: 'Checking the photo',
    processing: 'Reading the weave',
    needs_review: 'Ready for review',
    refused: 'Refused',
    approved: 'Approved',
    printed: 'Printed',
    voided: 'Voided',
    error: 'Error',
  }[state];
}
